/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the publish dialog (SPEC_WORKFLOW §7, plan H4).

   ⛔ Tweaks 35 (docs/MODULES_VISION.md "Propagation = PR, never silent
   mutation"): this dialog LISTS the variants the publish will offer its
   changes to — count + names, one row each — but never decides for them.
   Each variant answers in its own Updates panel, with its words unchanged
   until someone accepts. Nothing is written until Publish — then ONE batch
   (useSpecMasterDraft.commitPublish).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import type { ClientLayer } from '../../shared/types';
import { pendingCount, type VariantChangeEntry } from '../../shared/variant-updates';
import { affectedEntries, type PublishPlan } from './publishPlan';

export function variantNameOf(layers: ClientLayer[], entry: VariantChangeEntry): string {
  return layers.find((l) => l.id === entry.layerId)?.name || 'Variant';
}

export function PublishDialog({
  plan,
  notice,
  busy,
  onCancel,
  onPublish,
}: {
  plan: PublishPlan;
  /** Inline notice (the draft moved while the dialog was open). */
  notice?: string | null;
  busy: boolean;
  onCancel: () => void;
  onPublish: () => void;
}) {
  const affected = React.useMemo(() => affectedEntries(plan), [plan]);
  const names = affected.map((e) => variantNameOf(plan.layers, e)).join(', ');
  const blocksLine = `${plan.changedBlocks} block${plan.changedBlocks === 1 ? '' : 's'} changed`;
  const n = affected.length;

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent
        className="sps-publish-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Publish v{plan.toVersion}</DialogTitle>
          <DialogDescription>
            {n
              ? `${blocksLine} · ${n} variant${n === 1 ? '' : 's'} affected: ${names}`
              : `${blocksLine}. No variant is affected — the draft becomes the published version.`}
          </DialogDescription>
        </DialogHeader>

        {notice && (
          <p className="sps-pub-notice" role="alert">
            {notice}
          </p>
        )}

        {n > 0 && (
          <div className="sps-pub-body">
            <p className="sps-pub-lead">
              Each variant gets these changes as updates to accept or reject in its own Updates
              panel. A variant&rsquo;s words stay as they are until someone accepts.
            </p>
            <ul className="sps-pub-rows">
              {affected.map((entry) => {
                const k = pendingCount(entry);
                return (
                  <li key={entry.id} className="sps-pub-row">
                    <div className="sps-pub-row-head">
                      <span className="sps-pub-row-name">{variantNameOf(plan.layers, entry)}</span>
                      <span className="sps-pub-row-count">
                        {k} update{k === 1 ? '' : 's'} to review
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onPublish} disabled={busy}>
            {busy ? 'Publishing…' : `Publish v${plan.toVersion}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
